import type { AccionTarea, ConfiguracionIA, ActionNode, ActionEdge, ActionNodeData, FlowStats } from '../types';
import { getAccionIAConfig } from './parseService';

// Node colors based on type
const NODE_COLORS = {
  inicio: { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' },
  grupo: { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
  accion: { bg: '#f8fafc', border: '#e2e8f0', text: '#475569' },
  seccion: { bg: '#fce7f3', border: '#ec4899', text: '#9d174d' },
  checkpoint: { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' },
  posta: { bg: '#e0e7ff', border: '#6366f1', text: '#3730a3' },
  alternativa: { bg: '#fef9c3', border: '#eab308', text: '#854d0e' },
  fin: { bg: '#fef2f2', border: '#ef4444', text: '#991b1b' },
  ia: { bg: '#ecfdf5', border: '#10b981', text: '#065f46' },
  iaGenera: { bg: '#d1fae5', border: '#059669', text: '#047857' },
};

// Build nodes and edges from actions
export function buildFlowFromAcciones(
  acciones: AccionTarea[],
  configIA: ConfiguracionIA | null
): { nodes: ActionNode[]; edges: ActionEdge[] } {
  const nodes: ActionNode[] = [];
  const edges: ActionEdge[] = [];

  // Create maps for quick lookup
  const accionMap = new Map<string, AccionTarea>();
  const codigoUnicoMap = new Map<string, AccionTarea>();

  acciones.forEach(accion => {
    accionMap.set(accion.objectId, accion);
    if (accion.CodigoUnico) {
      codigoUnicoMap.set(accion.CodigoUnico, accion);
    }
  });

  // Separate root actions (no parent) from children
  const rootAcciones = acciones.filter(a => !a.Padre);
  const childrenMap = new Map<string, AccionTarea[]>();

  acciones.forEach(accion => {
    if (accion.Padre) {
      const parentId = accion.Padre.objectId;
      if (!childrenMap.has(parentId)) {
        childrenMap.set(parentId, []);
      }
      childrenMap.get(parentId)!.push(accion);
    }
  });

  // Sort children by OrdenHijo or Orden
  childrenMap.forEach((children) => {
    children.sort((a, b) => (a.OrdenHijo || a.Orden) - (b.OrdenHijo || b.Orden));
  });

  // Sort root actions by Orden
  rootAcciones.sort((a, b) => a.Orden - b.Orden);

  // Track which nodes have been processed
  const processedNodes = new Set<string>();
  let nodeIndex = 1;

  // Process a single action (only creates nodes, not edges)
  function processAction(
    accion: AccionTarea,
    parentId?: string,
    _isChild = false
  ): void {
    if (processedNodes.has(accion.objectId)) return;
    processedNodes.add(accion.objectId);

    const nodeId = accion.CodigoUnico || accion.objectId;

    // Determine node type
    let tipo: ActionNodeData['tipo'] = 'accion';
    const tipoLower = (accion.Tipo || '').toLowerCase();

    if (tipoLower.includes('seccion') || tipoLower === 'section') {
      tipo = 'seccion';
    } else if (tipoLower.includes('grupo') || tipoLower === 'group') {
      tipo = 'grupo';
    } else if (accion.Posta) {
      tipo = 'posta';
    } else if (tipoLower.includes('checkpoint')) {
      tipo = 'checkpoint';
    } else if (accion.AlternativaOpcion !== undefined && accion.AlternativaOpcion > 0) {
      tipo = 'alternativa';
    }

    // Check for IA configuration
    const iaInfo = getAccionIAConfig(accion.objectId, nodeId, configIA);

    // Get colors based on type and IA
    let colors = NODE_COLORS[tipo] || NODE_COLORS.accion;
    if (iaInfo.tieneIA) {
      colors = iaInfo.config?.generarRespuesta ? NODE_COLORS.iaGenera : NODE_COLORS.ia;
    }

    // Build label (no emojis - Miro style)
    const label = accion.Texto || `Acción ${accion.Orden}`;

    // Create node
    const node: ActionNode = {
      id: nodeId,
      type: 'actionNode',
      position: { x: 0, y: 0 }, // Will be calculated by layout
      data: {
        label,
        codigoUnico: nodeId,
        accionId: accion.objectId,
        tipo,
        orden: nodeIndex++,
        tipoAccion: accion.Tipo,
        tieneIA: iaInfo.tieneIA,
        iaConfig: iaInfo.config,
        esSeccion: tipo === 'seccion',
        esPosta: tipo === 'posta',
        esCheckpoint: tipo === 'checkpoint',
        esAlternativa: tipo === 'alternativa',
        saltaA: accion.SaltarA,
        tieneSiguiente: !accion.Finaliza,
        padreId: parentId,
      },
      style: {
        backgroundColor: colors.bg,
        borderColor: colors.border,
        color: colors.text,
        borderWidth: iaInfo.tieneIA ? 3 : 2,
        borderStyle: 'solid',
      },
    };

    nodes.push(node);

    // Process children if this is a group
    const children = childrenMap.get(accion.objectId) || [];
    children.forEach(child => {
      processAction(child, nodeId, true);
    });
  }

  // PHASE 1: Create ALL nodes first
  rootAcciones.forEach(accion => {
    processAction(accion);
  });

  // Build node lookup map for edge creation
  const nodeMap = new Map<string, ActionNode>();
  nodes.forEach(node => {
    nodeMap.set(node.id, node);
    if (node.data.codigoUnico) {
      nodeMap.set(node.data.codigoUnico, node);
    }
  });

  // PHASE 2: Create ALL edges after all nodes exist

  // 2a. Create parent-child edges
  acciones.forEach(accion => {
    if (accion.Padre) {
      const parentAccion = accionMap.get(accion.Padre.objectId);
      if (parentAccion) {
        const parentId = parentAccion.CodigoUnico || parentAccion.objectId;
        const childId = accion.CodigoUnico || accion.objectId;
        edges.push({
          id: `edge-child-${parentId}-${childId}`,
          source: parentId,
          target: childId,
          type: 'default', // Bezier curve
          animated: false,
          style: { stroke: '#cbd5e1' },
          data: { tipo: 'siguiente' },
        });
      }
    }
  });

  // 2b. Create sequential flow edges between root nodes
  // IMPORTANT: If an action has SaltarA, it does NOT continue to the next action
  for (let i = 0; i < rootAcciones.length - 1; i++) {
    const currentAccion = rootAcciones[i];
    const nextAccion = rootAcciones[i + 1];

    const currentNodeId = currentAccion.CodigoUnico || currentAccion.objectId;
    const nextNodeId = nextAccion.CodigoUnico || nextAccion.objectId;

    // Only add edge if current action doesn't have a jump or finalize
    // (if it jumps or finalizes, it doesn't go to the next sequential action)
    if (!currentAccion.SaltarA && !currentAccion.Finaliza) {
      edges.push({
        id: `edge-seq-${currentNodeId}-${nextNodeId}`,
        source: currentNodeId,
        target: nextNodeId,
        type: 'default', // Bezier curve
        animated: false,
        style: { stroke: '#94a3b8' },
        data: { tipo: 'siguiente' },
      });
    }
  }

  // 2c. Create jump edges (SaltarA) - NOW all nodes exist!
  acciones.forEach(accion => {
    if (accion.SaltarA) {
      const sourceId = accion.CodigoUnico || accion.objectId;
      const jumpTargets = Array.isArray(accion.SaltarA) ? accion.SaltarA : [accion.SaltarA];

      jumpTargets.forEach(target => {
        // Convert target to string for consistent lookup
        const targetStr = String(target);

        // Try to find target node by various methods
        let targetNode = nodeMap.get(targetStr);

        // If not found and target has format "codigoUnico-X-Y", try the base part
        if (!targetNode && targetStr.includes('-')) {
          const basePart = targetStr.split('-')[0];
          targetNode = nodeMap.get(basePart);
        }

        // If not found, try to find by objectId in accionMap then get node
        if (!targetNode) {
          const targetAccion = accionMap.get(targetStr) || codigoUnicoMap.get(targetStr);
          if (targetAccion) {
            const targetNodeId = targetAccion.CodigoUnico || targetAccion.objectId;
            targetNode = nodeMap.get(targetNodeId);
          }
        }

        // Also try to find by matching node data
        if (!targetNode) {
          targetNode = nodes.find(n =>
            n.id === targetStr ||
            n.data.codigoUnico === targetStr ||
            n.data.accionId === targetStr
          );
        }

        // Last resort: try partial match on codigoUnico
        if (!targetNode) {
          const basePart = targetStr.split('-')[0];
          targetNode = nodes.find(n =>
            n.data.codigoUnico === basePart ||
            n.id === basePart
          );
        }

        if (targetNode) {
          edges.push({
            id: `edge-jump-${sourceId}-${targetStr}`,
            source: sourceId,
            target: targetNode.id,
            type: 'default', // Bezier curve
            animated: true,
            style: { stroke: '#f59e0b', strokeWidth: 2 },
            label: 'Salta a',
            labelStyle: { fill: '#92400e', fontSize: 10, fontWeight: 500 },
            data: { tipo: 'salto', label: 'Salta a' },
          });
        }
      });
    }
  });

  // 2d. Detect orphan nodes (not reachable from the first action)
  // Build reverse edge map for reachability check
  const reachable = new Set<string>();
  const edgeTargets = new Map<string, string[]>();
  edges.forEach(edge => {
    if (!edgeTargets.has(edge.source)) {
      edgeTargets.set(edge.source, []);
    }
    edgeTargets.get(edge.source)!.push(edge.target);
  });

  // BFS from first action to find all reachable nodes
  if (rootAcciones.length > 0) {
    const firstNodeId = rootAcciones[0].CodigoUnico || rootAcciones[0].objectId;
    const queue = [firstNodeId];
    reachable.add(firstNodeId);

    while (queue.length > 0) {
      const current = queue.shift()!;
      const targets = edgeTargets.get(current) || [];
      targets.forEach(target => {
        if (!reachable.has(target)) {
          reachable.add(target);
          queue.push(target);
        }
      });
    }
  }

  // Mark orphan nodes with reduced opacity
  nodes.forEach(node => {
    if (!reachable.has(node.id)) {
      node.data.isOrphan = true;
      node.style = {
        ...node.style,
        opacity: 0.5,
      };
    }
  });

  return { nodes, edges };
}

// Calculate flow statistics
export function calculateFlowStats(nodes: ActionNode[], edges: ActionEdge[]): FlowStats {
  const stats: FlowStats = {
    totalNodes: nodes.length,
    totalEdges: edges.length,
    grupos: 0,
    acciones: 0,
    postas: 0,
    checkpoints: 0,
    conIA: 0,
    sinIA: 0,
  };

  nodes.forEach(node => {
    switch (node.data.tipo) {
      case 'grupo':
        stats.grupos++;
        break;
      case 'posta':
        stats.postas++;
        break;
      case 'checkpoint':
        stats.checkpoints++;
        break;
      default:
        stats.acciones++;
    }

    if (node.data.tieneIA) {
      stats.conIA++;
    } else {
      stats.sinIA++;
    }
  });

  return stats;
}

// Apply dagre layout to nodes
export function applyDagreLayout(
  nodes: ActionNode[],
  edges: ActionEdge[],
  orientation: 'LR' | 'TB' = 'LR'
): ActionNode[] {
  // Simple grid layout for now (dagre would require additional library)
  const nodeSpacing = orientation === 'LR' ? { x: 300, y: 120 } : { x: 200, y: 150 };
  const startPos = { x: 50, y: 50 };

  // Group nodes by level (distance from first node)
  const levels = new Map<string, number>();
  const visited = new Set<string>();
  const edgeMap = new Map<string, string[]>();

  // Build edge map
  edges.forEach(edge => {
    if (!edgeMap.has(edge.source)) {
      edgeMap.set(edge.source, []);
    }
    edgeMap.get(edge.source)!.push(edge.target);
  });

  // BFS to calculate levels - start from first node (not INICIO which was removed)
  function bfs() {
    // Find the first node (the one with orden=1 or just the first in the array)
    const firstNode = nodes.find(n => n.data.orden === 1) || nodes[0];
    if (!firstNode) return;

    const queue: Array<{ id: string; level: number }> = [{ id: firstNode.id, level: 0 }];

    while (queue.length > 0) {
      const { id, level } = queue.shift()!;

      if (visited.has(id)) continue;
      visited.add(id);
      levels.set(id, level);

      const targets = edgeMap.get(id) || [];
      targets.forEach(target => {
        if (!visited.has(target)) {
          queue.push({ id: target, level: level + 1 });
        }
      });
    }

    // Handle orphan nodes - give them a default level based on their orden
    nodes.forEach(node => {
      if (!levels.has(node.id)) {
        levels.set(node.id, node.data.orden || 0);
      }
    });
  }

  bfs();

  // Separate reachable nodes from orphans
  const reachableNodes: ActionNode[] = [];
  const orphanNodes: ActionNode[] = [];

  nodes.forEach(node => {
    if (node.data.isOrphan) {
      orphanNodes.push(node);
    } else {
      reachableNodes.push(node);
    }
  });

  // Group reachable nodes by level
  const nodesByLevel = new Map<number, ActionNode[]>();
  reachableNodes.forEach(node => {
    const level = levels.get(node.id) ?? 0;
    if (!nodesByLevel.has(level)) {
      nodesByLevel.set(level, []);
    }
    nodesByLevel.get(level)!.push(node);
  });

  // Position reachable nodes
  const positionedReachable = reachableNodes.map(node => {
    const level = levels.get(node.id) ?? 0;
    const nodesAtLevel = nodesByLevel.get(level) || [];
    const indexAtLevel = nodesAtLevel.indexOf(node);
    const totalAtLevel = nodesAtLevel.length;

    let x: number, y: number;

    if (orientation === 'LR') {
      x = startPos.x + level * nodeSpacing.x;
      y = startPos.y + (indexAtLevel - (totalAtLevel - 1) / 2) * nodeSpacing.y;
    } else {
      x = startPos.x + (indexAtLevel - (totalAtLevel - 1) / 2) * nodeSpacing.x;
      y = startPos.y + level * nodeSpacing.y;
    }

    return {
      ...node,
      position: { x, y },
    };
  });

  // Position orphan nodes in a separate row below
  // Sort orphans by their orden to maintain visual order
  orphanNodes.sort((a, b) => (a.data.orden || 0) - (b.data.orden || 0));

  const orphanOffset = orientation === 'LR'
    ? { x: 0, y: nodeSpacing.y * 2.5 }  // Below main flow
    : { x: nodeSpacing.x * 2.5, y: 0 }; // Right of main flow

  const positionedOrphans = orphanNodes.map((node, index) => {
    let x: number, y: number;

    if (orientation === 'LR') {
      x = startPos.x + index * nodeSpacing.x;
      y = startPos.y + orphanOffset.y;
    } else {
      x = startPos.x + orphanOffset.x;
      y = startPos.y + index * nodeSpacing.y;
    }

    return {
      ...node,
      position: { x, y },
    };
  });

  return [...positionedReachable, ...positionedOrphans];
}
